import React from 'react';
import { useLanguage } from '../i18n';

const InformationPopup = ({ onClose }) => {
  const { t, language } = useLanguage();

  const userTips = {
    sv: {
      title: "Information – Översiktlig beskrivning av flyghinderanalys",
      content: [
        {
          html:
            "<strong>Syfte och funktion</strong><br/>" +
            "Systemet genomför en integrerad flyghinderanalys där varje objekt (t.ex. byggnader, master, kranar, terrängpunkter) jämförs mot relevanta regelverk och tekniska krav. Analysen utvärderar påverkan på:<br/><ul>" +
            "<li>Hinderbegränsande ytor</li>" +
			"<li>eTOD ytor</li>" +
            "<li>Procedurytor</li>" +
            "<li>Luftrum</li>" +
            "<li>ATS-krav</li>" +
            "<li>Teknisk infrastruktur</li></ul>" +
            "Syftet är att ge beslutsstöd för att avgöra om ett objekt:<br/><ul>" +
            "<li>Kan godkännas i befintligt skick</li>" +
            "<li>Kräver åtgärder (t.ex. höjdreduktion, tidsbegränsning eller operativa restriktioner)</li>" +
            "<li>Behöver vidare regulatorisk prövning</li></ul>"
        },
        {
          html:
            "<strong>Analysområden</strong>"
        },
        {
          html:
            "<strong>1. Hinderbegränsande ytor (ICAO Annex 14, EASA CS-ADR-DSN)</strong><br/>" +
            "Systemet kontrollerar om objektet tränger in i några av de definierade hinderbegränsande ytorna (OLS), exempelvis:<br/><ul>" +
            "<li>Yttre/inre horisontell yta</li>" +
            "<li>Konisk yta</li>" +
            "<li>Inflygningsytor</li>" +
            "<li>Startstigningsyta</li>" +
            "<li>Övergångsytor</li>" +
			"<li>Stråk och visuella referensytor</li>" +
			"<li>Ytor för avbruten landning</li>" +
            "<li>OFZ</li></ul>" +
            "Fasta objekt får inte inkräkta på dessa ytor. Temporära hinder kan accepteras efter samråd, förutsatt att de inte påverkar flygprocedurer eller luftrumsanvändning.<br/><br/>" +
            "Resultatet klassificeras som:<br/><ul>" +
            "<li>Ingen påverkan</li>" +
            "<li>Påverkan (med vidare åtgärdsbehov)</li></ul>"
        },
        {
          html:
            "<strong>2. eTOD-ytor (ICAO Annex 15)</strong><br/>" +
            "Objektets position och höjd jämförs med befintliga eTOD ytor. Syftet är att:<br/><ul>" +
            "<li>Identifiera nya eller förändrade objekt</li>" +
            "<li>Bedöma behov av uppdatering i eTOD-leveranser till AIM</li></ul>"
        },
        {
          html:
            "<strong>3. Luftrumspåverkan (ICAO PANS-ATM Doc 4444)</strong><br/>" +
            "Systemet analyserar om objektet påverkar det kontrollerade luftrummet, t.ex.:<br/><ul>" +
            "<li>Publicerade sektorer</li>" +
            "<li>Holdingmönster</li>" +
            "<li>In- och utflygningsvägar</li></ul>"
        },
        {
          html:
            "<strong>4. ATS vektoreringhöjder (ICAO PANS-ATM Doc 4444)</strong><br/>" +
            "Systemet bedömer om objektet påverkar Minimum Vectoring Altitudes (MVA) eller andra minimihöjder. Analysen tar hänsyn till:<br/><ul>" +
            "<li>Objektets höjd över havet (AMSL)</li>" +
            "<li>Hinderfrihetskrav</li></ul>" +
            "Vid behov föreslås justerad minsta vektoreringhöjd per sektor, vilket stödjer airspace design och säkerhetsbedömning."
        },
        {
          html:
            "<strong>5. Flygprocedurer (ICAO Doc 8168, Doc 9905)</strong><br/>" +
            "Systemet utvärderar påverkan på instrumentflygprocedurer (IFP), såsom:<br/><ul>" +
            "<li>STAR, SID</li>" +
            "<li>ILS/LOC</li>" +
            "<li>RNP APCH (LNAV, LNAV/VNAV, LPV)</li>" +
            "<li>VOR/NDB-procedurer</li>" +
            "<li>MSA, TAA, holding</li></ul>" +
            "Påverkan visas per procedurtyp och fas (initial, intermediate, final, missed approach). Resultaten kan exporteras för vidare granskning av PANS-OPS-specialist."
        },
        {
          html:
            "<strong>6. Teknisk utrustning (ICAO Doc 015)</strong><br/>" +
            "Systemet kontrollerar eventuell påverkan på kritisk navigations- och övervakningsutrustning, såsom:<br/><ul>" +
            "<li>ILS (lokalizer och glidbana)</li>" +
            "<li>DME, VOR, NDB</li></ul>" +
            "Objekt som riskerar att försämra prestandan flaggas. Om objektet befinner sig inom ILS-kritiska eller känsliga områden krävs särskild utredning innan godkännande."
        },
        {
          html:
            "<strong>Ansvarsfriskrivning:</strong><br/>" +
            "Den automatiska analysen ersätter inte formell myndighetsprövning eller PANS‑OPS‑designvalidering. Slutligt beslut om acceptans fattas av behörig luftfartsmyndighet/flygplatsoperatör."
        }
      ]
    },
    en: {
      title: "Information – Overview of obstacle analysis",
      content: [
        {
          html:
            "<strong>Purpose and function</strong><br/>" +
            "The system performs an integrated aeronautical obstacle analysis where each object (e.g. buildings, masts, cranes, terrain points) is compared against relevant regulations and technical requirements. The analysis evaluates impact on:<br/><ul>" +
            "<li>Obstacle limitation surfaces</li>" +
			"<li>eTOD surfaces</li>" +
            "<li>Procedure protection surfaces</li>" +
            "<li>Airspace</li>" +
            "<li>ATS requirements</li>" +
            "<li>Technical infrastructure</li></ul>" +
            "The purpose is to support decisions to determine whether an object:<br/><ul>" +
            "<li>Can be accepted as-is</li>" +
            "<li>Requires mitigation (e.g. height reduction, time limitation, operational restrictions)</li>" +
            "<li>Needs further regulatory assessment</li></ul>"
        },
        {
          html:
            "<strong>Analysis domains</strong>"
        },
        {
          html:
            "<strong>1. Obstacle Limitation Surfaces (ICAO Annex 14, EASA CS-ADR-DSN)</strong><br/>" +
            "The system checks whether the object penetrates any of the defined Obstacle Limitation Surfaces (OLS), such as:<br/><ul>" +
            "<li>Outer/inner horizontal surface</li>" +
            "<li>Conical surface</li>" +
            "<li>Approach surfaces</li>" +
            "<li>Take-off climb surface</li>" +
            "<li>Transition surfaces</li>" +
			"<li>Strip and visual reference surfaces</li>" +
			"<li>Balked landing surfaces</li>" +
            "<li>OFZ</li></ul>" +
            "Permanent objects must not infringe on these surfaces. Temporary obstacles may be accepted after coordination, provided they do not affect flight procedures or airspace usage.<br/><br/>" +
            "Results are classified as:<br/><ul>" +
            "<li>No impact</li>" +
            "<li>Impact (requiring further action)</li></ul>"
        },
        {
          html:
            "<strong>2. eTOD surfaces (ICAO Annex 15)</strong><br/>" +
            "The object's position and height are compared with existing eTOD surfaces. The purpose is to:<br/><ul>" +
            "<li>Identify new or changed objects</li>" +
            "<li>Assess the need for updates in eTOD submissions to AIM</li></ul>"
        },
        {
          html:
            "<strong>3. Airspace impact (ICAO PANS-ATM Doc 4444)</strong><br/>" +
            "The system analyses whether the object affects controlled airspace, such as:<br/><ul>" +
            "<li>Published sectors</li>" +
            "<li>Holding patterns</li>" +
            "<li>Arrival and departure routes</li></ul>"
        },
        {
          html:
            "<strong>4. ATS vectoring altitudes (ICAO PANS-ATM Doc 4444)</strong><br/>" +
            "The system assesses whether the object affects Minimum Vectoring Altitudes (MVA) or other minimum altitudes. The analysis considers:<br/><ul>" +
            "<li>Object elevation above sea level (AMSL)</li>" +
            "<li>Obstacle clearance requirements</li></ul>" +
            "Where necessary, an adjusted minimum vectoring altitude per sector is proposed, supporting airspace design and safety assessment."
        },
        {
          html:
            "<strong>5. Flight procedures (ICAO Doc 8168, Doc 9905)</strong><br/>" +
            "The system evaluates impact on instrument flight procedures (IFP), such as:<br/><ul>" +
            "<li>STAR, SID</li>" +
            "<li>ILS/LOC</li>" +
            "<li>RNP APCH (LNAV, LNAV/VNAV, LPV)</li>" +
            "<li>VOR/NDB procedures</li>" +
            "<li>MSA, TAA, holding</li></ul>" +
            "Impacts are displayed by procedure type and phase (initial, intermediate, final, missed approach). Results can be exported for further review by a PANS-OPS specialist."
        },
        {
          html:
            "<strong>6. Technical equipment (ICAO Doc 015)</strong><br/>" +
            "The system checks possible impact on critical navigation and surveillance equipment, such as:<br/><ul>" +
            "<li>ILS (localizer and glide path)</li>" +
            "<li>DME, VOR, NDB</li></ul>" +
            "Objects that may degrade performance are flagged. If the object is within ILS critical or sensitive areas, special assessment is required before approval."
        },
        {
          html:
            "<strong>Disclaimer:</strong><br/>" +
            "The automated analysis does not replace formal authority approval or PANS-OPS design validation. Final acceptance decisions rest with the competent aviation authority / aerodrome operator."
        }
      ]
    }
  };

  const tips = userTips[language] || userTips.en;

  return (
    <div className="popup-overlay">
      <div className="popup-content">
        <div className="popup-header">
          <h2>{tips.title}</h2>
          <button className="popup-close" onClick={onClose} aria-label={t('close')}>
            ×
          </button>
        </div>
        <div className="popup-body">
          {tips.content.map((tip, index) => (
            <div key={index} className="tip-item">
              <div className="tip-text">
                {tip.html ? (
                  <div
                    className="rich-text"
                    style={{ whiteSpace: 'normal' }}
                    dangerouslySetInnerHTML={{ __html: tip.html }}
                  />
                ) : (
                  <p style={{ whiteSpace: 'pre-line' }}>{tip.text}</p>
                )}
              </div>
            </div>
          ))}
        </div>
        <div className="popup-footer">
          <button className="btn-primary" onClick={onClose}>
            {t('close')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InformationPopup;